import {
  ButtonClean,
  ButtonLight,
  ButtonPrimary
} from 'front-end/src/components'
import { Modal } from '../../../components/common/modal/Modal'
import { t, Trans } from '@lingui/macro'
import React, { FC, useCallback, useContext, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { last } from 'lodash'
import { notification } from '../../../components/common/notifications/Notifications'
import parsePptxSlides from '../../../utils/pptx-parser/pptxParser'
import ReactQuill from '@capaj/react-quill'
import { useTopicForEditQuery } from '../TopicEditPage'
import { apolloClientPrivate } from '../../../graphql/apolloClient'

import Loader from '../../../components/common/loader/Loader'

import IconFileUpload from 'public/assets/img/file-upload.svg'
import './PowerPointImportModal.less'
import classNames from 'classnames'
import {
  CreateTextElementsMutation,
  CreateTextElementsDocument
} from './PowerPointImportModal.codegen'
import { TagBeta } from 'front-end/src/components/common/tag/Tag'
import { Flex, Text } from '@chakra-ui/react'
import { TopicEditingContext } from '../TopicEditContext'

interface IProps {
  onModalClose: () => void
  isShown: boolean
  sanitizationQuillEditorRef: React.RefObject<ReactQuill>
}

const PowerPointImportModal: FC<IProps> = (props) => {
  const onDropRejected = useCallback(() => {
    notification.error(t`File type is not supported or the file is too big.`)
  }, [])

  const [creating, setCreating] = useState(false)
  const { topic } = useContext(TopicEditingContext)
  const { refetch: refetchTopic } = useTopicForEditQuery()

  const [acceptedFile, setAcceptedFile] = useState<File | null>(null)

  const createElements = async (elements: string[]) => {
    setCreating(true)
    try {
      await apolloClientPrivate.mutate<CreateTextElementsMutation>({
        mutation: CreateTextElementsDocument,
        variables: {
          topicId: topic.id,
          contentArray: elements
        }
      })

      await refetchTopic()
      props.onModalClose()
      setAcceptedFile(null)
    } catch (err) {
      console.error(err)
    } finally {
      setCreating(false)
    }
  }

  const sanitizeHtmlUsingQuill = (htmlContent: string) => {
    if (!props.sanitizationQuillEditorRef.current) {
      return ''
    }
    props.sanitizationQuillEditorRef.current
      .getEditor()
      .clipboard.dangerouslyPasteHTML(htmlContent)
    // @ts-expect-error
    return props.sanitizationQuillEditorRef.current.state?.value || ''
  }

  const onDropAccepted = useCallback(([file]: File[]) => {
    const { name } = file
    const extension = last(name.split('.'))

    if (!extension || extension.toLowerCase() !== 'pptx') {
      onDropRejected()
    }

    setAcceptedFile(file)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: '.pptx',
    maxSize: 1024 * 1024 * 1024,
    onDropRejected,
    onDropAccepted
  })

  const processFile = () => {
    if (!acceptedFile) {
      notification.error(
        t`No file chosen. Please choose some PPTX file to create elements from.`
      )
      return
    }
    const reader = new FileReader()
    reader.onabort = () =>
      notification.error(t`Error while reading file, please try again later.`)

    reader.onerror = () =>
      notification.error(t`Error while reading file, please try again later.`)

    reader.onload = async () => {
      const arrayBuffer = reader.result
      if (!(arrayBuffer instanceof ArrayBuffer)) {
        notification.error(t`No slides found in provided PPTX file.`)
        return
      }
      let pptxSlides: string[] = []
      try {
        pptxSlides = await parsePptxSlides(arrayBuffer as ArrayBuffer)
      } catch (e) {
        notification.error(e)
      }
      pptxSlides = pptxSlides
        .map(sanitizeHtmlUsingQuill)
        .filter((slideAsArrayBuffer) => {
          return slideAsArrayBuffer
        })

      if (pptxSlides.length > 0) {
        await createElements(pptxSlides)
      } else {
        notification.error(t`Unable to parse pptx file`)
      }
    }

    reader.readAsArrayBuffer(acceptedFile)
  }

  const dropzoneClasses = classNames('PowerpointModal__dropzone', {
    'PowerpointModal__dropzone--active-drag': isDragActive
  })

  return (
    <Modal
      className="PowerpointModal"
      header={<Trans>Import elements from file</Trans>}
      subHeader={
        <Flex flexDirection="column">
          <Trans>
            Uploaded file will be transformed into elements of this resource..
          </Trans>
          <Flex alignItems="center" justifyContent="center">
            <TagBeta />
            <Text ml="4px">
              <Trans>This is experimental feature.</Trans>
            </Text>
          </Flex>
        </Flex>
      }
      show={props.isShown}
      controls={
        <>
          <ButtonClean
            onClick={() => {
              props.onModalClose()
              setAcceptedFile(null)
            }}
          >
            <Trans>Cancel</Trans>
          </ButtonClean>
          <ButtonPrimary onClick={processFile}>
            <Trans>Process file</Trans>
          </ButtonPrimary>
        </>
      }
    >
      {creating && (
        <div className="choose-page">
          <h3>
            <Trans>Please wait</Trans>
          </h3>
          <Loader />
          <p>
            <Trans>Creating a new elements from PowerPoint file...</Trans>
          </p>
        </div>
      )}
      {!creating && (
        <div {...getRootProps()} className={dropzoneClasses}>
          <div>
            <img src={IconFileUpload} alt={t`File upload`} />
          </div>
          <div>
            <div className="PowerpointModal__dropzone__text">
              <Trans>Drag your file here or</Trans>
            </div>
            <div className="PowerpointModal__dropzone__inputs">
              <input {...getInputProps()} />
              <ButtonLight>
                <Trans>Select file to upload</Trans>
              </ButtonLight>
              <div className="PowerpointModal__dropzone__filename">
                {acceptedFile && acceptedFile.name}
              </div>
            </div>
            <div className="PowerpointModal__dropzone__desc">
              <Trans>We accept .pptx files up to 1 GB</Trans>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default PowerPointImportModal
